import { useState } from 'react';
import { Activity } from '@models/activity.model';
import { ActivityHeader } from '../ActivityHeader';
import { formatActivityTime } from '@utils/formatDate';
import { Card } from '@components/atoms/Card';
import Alert from '@components/atoms/Alert';
import { TaskName } from '../TaskName';
import ProjectGeofenceModal from '@components/organisms/ProjectGeofenceModal';
import { GeofenceCategoryIcon } from '@utils/geofenceCategoryIcons';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import { useTranslation } from '@hooks/useTranslation';

export const ActivityPunchClock = ({
  activity,
  handleOpenTaskDetail,
}: {
  activity: Activity;
  handleOpenTaskDetail?: (taskId: string) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const [isGeofenceModalOpen, setIsGeofenceModalOpen] = useState(false);

  const punch = activity.punchClock;
  const action =
    punch?.action === 'ENTER' ? tActivityCards.punchClock.clockedIn : tActivityCards.punchClock.clockedOut;
  const isEnter = punch?.action === 'ENTER';
  const timeLabel = punch?.time
    ? formatActivityTime(punch.time)
    : activity.createdAt
      ? formatActivityTime(activity.createdAt as any)
      : '';

  const projectName =
    punch?.project?.name || activity.project?.name || tActivityCards.notAvailable;
  const userName = punch?.user
    ? `${punch.user.firstName} ${punch.user.lastName}`
    : `${activity.user?.firstName || ''} ${activity.user?.lastName || ''}`.trim() ||
      tActivityCards.punchClock.defaultUser;

  const geofence = punch?.geofence ?? null;
  const project = activity.project ?? punch?.project ?? null;
  const modalProject = project as any;

  const rightLabel = (
    <div className="flex items-center gap-2">
      <span
        className={isEnter ? 'text-green-600 text-sm' : 'text-red-600 text-sm'}>
        {action}
      </span>
      {timeLabel && <p className="text-sm text-gray-500">- {timeLabel}</p>}
    </div>
  );

  return (
    <Card>
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      <div className="mt-4 space-y-3">
        {/* Context Information Section */}
        <Alert>
          <div className="text-sm text-gray-700 space-y-1">
            <p>
              <span className="font-medium text-gray-900">{tActivityCards.punchClock.userLabel}</span>{' '}
              {userName}
            </p>
            <div className="flex items-start justify-between gap-2">
              <p>
                <span className="font-medium text-gray-900">{tActivityCards.projectLabel}</span>{' '}
                {projectName}
              </p>
              {project?.projectId && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsGeofenceModalOpen(true);
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-indigo-700 bg-white ring-1 ring-indigo-200 hover:bg-indigo-50 flex-shrink-0"
                >
                  <PinDropOutlinedIcon sx={{ fontSize: 14 }} />
                  {tActivityCards.punchClock.geofenceButton}
                </button>
              )}
            </div>
            {activity.task?.name && (
              <TaskName
                task={activity.task}
                size="sm"
                taskId={activity.taskId || activity.task?.taskId}
                onOpenTask={handleOpenTaskDetail}
              />
            )}
          </div>
        </Alert>

        {geofence && (
          <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2">
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: `${geofence.category?.color || geofence.color || '#6366F1'}20`,
                color: geofence.category?.color || geofence.color || '#6366F1',
              }}
            >
              <GeofenceCategoryIcon
                iconKey={geofence.category?.icon}
                sx={{ fontSize: 16 }}
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {geofence.name}
              </p>
              {geofence.category?.name && (
                <p className="text-xs text-gray-500 truncate">
                  {geofence.category.name}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Punch Clock Panel */}
        <Alert variant={isEnter ? 'success' : 'error'} border>
          <div className="text-sm space-y-2">
            <div className="flex items-center justify-between">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  isEnter
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                ⏱️ {action.toUpperCase()}
              </span>
              {timeLabel && (
                <span className="text-gray-600 text-xs">{timeLabel}</span>
              )}
            </div>

            {punch?.project?.name && (
              <p className="text-gray-700">
                <span className="font-medium text-gray-900">{tActivityCards.projectLabel}</span>{' '}
                {punch.project.name}
              </p>
            )}
          </div>
        </Alert>

        {activity.description && (
          <Alert className="bg-gray-50 rounded-md p-3">
            <p className="text-gray-800 text-sm leading-relaxed">
              {activity.description}
            </p>
          </Alert>
        )}
      </div>

      {project?.projectId && (
        <ProjectGeofenceModal
          isOpen={isGeofenceModalOpen}
          onClose={() => setIsGeofenceModalOpen(false)}
          project={modalProject}
          hideActiveToggle
          initialGeofenceId={geofence?.geofenceId ?? null}
        />
      )}
    </Card>
  );
};
